import { BADGE_CATEGORY, BADGE_RARITY, BadgeDefinition, EarnedBadge, UserBadgesResponse } from "@shared/models";

/**
 * The joined view-model one `BadgeCard` renders. Built once per render by `BadgesSection` (phase-36
 * plan D3) by iterating `badgeDefinitions` (the catalog — has name/description/icon for every badge,
 * earned or not) and looking each definition up in a `Map` built from `badges.earned` (has no
 * description at all) to decide earned/locked and to read `awardedAt` when earned. Never iterate
 * `earned` as the primary list — a definition with no matching row is locked, not absent.
 */
export interface BadgeCardViewModel {
  definitionId: number;
  // The badge's own api `code`, read only by `resolveBadgeIcon` (badgeArt.ts) to find its bundled art.
  // Both builders of this shape -- this file and earnedBadgeToViewModel.ts -- always set it; it is
  // optional only so the many existing test fixtures across this shelf that predate it still compile.
  code?: string;
  name: string;
  description: string | null;
  category: BADGE_CATEGORY;
  rarity: BADGE_RARITY;
  iconUrl: string | null;
  earned: boolean;
  awardedAt: string | null;
  // Renamed from `progressPercent` (overview-rebuild-pr3-plan.md, "progressPercent -> pointsToNext;
  // the bars go") — the redesigned card carries distance-to-go as text ("230 Sparks to go"), not a
  // bar, so the VALUE itself is now the api's own `points_to_next`, never a percentage. Same
  // non-null-iff-next-tier meaning as the field it replaces.
  pointsToNext: number | null;
}

/**
 * A card's `pointsToNext` is non-null only when it represents its ladder's own "next tier" —
 * `definition.tier === next_tier.index + 1`, not `=== next_tier.index` (which names the tier already
 * earned, and matches nothing for a user below tier 1, since `next_tier.index` is 0-based while
 * `badge_definitions.tier` is 1-based). The value itself is read straight off the api's own
 * `points_to_next` (`FlameProgress.points_to_next` / `PerValueProgress.points_to_next`,
 * `shared/models/Badge.tsx:41,50`), never computed as `next_tier.threshold - lifetime_total` —
 * overview-rebuild-pr3-plan.md "Distance-to-go is server-decided": `computeLadderTier`
 * (`api/app/services/awardEngine.service.ts:65-80`) is the sole authority and its `next_index`/
 * `points_to_next` always agree, so a second, web-side computation could only duplicate or diverge
 * from it. Every other locked card (achievement/tenure/seasonal, and any flame/value tier further out
 * than "next") gets `pointsToNext: null`.
 */
const computePointsToNext = (definition: BadgeDefinition, badges: UserBadgesResponse): number | null => {
  if (definition.category === BADGE_CATEGORY.FLAME) {
    const { next_tier, points_to_next } = badges.flame;
    if (next_tier !== null && definition.tier === next_tier.index + 1) {
      return points_to_next;
    }
    return null;
  }

  if (definition.category === BADGE_CATEGORY.VALUE) {
    // The `core_value_id` conjunct is load-bearing (phase-36 plan §3, test 6): two per-value ladders
    // can independently land their own "next tier" on the SAME tier number, so matching on tier number
    // alone (without also matching which core value this definition belongs to) would light up the
    // wrong ladder's badge. Carried over unchanged (overview-rebuild-pr3-plan.md: "Only the returned
    // value changes. Do not simplify the predicate while changing the return.").
    const perValueEntry = badges.per_value.find((entry) => entry.core_value_id === definition.core_value_id);
    if (perValueEntry && perValueEntry.next_tier !== null && definition.tier === perValueEntry.next_tier.index + 1) {
      return perValueEntry.points_to_next;
    }
    return null;
  }

  // Tenure/achievement/seasonal: deliberately not computed — no "next tier" meaning, locked/greyed only.
  return null;
};

// `BadgeCardViewModel` isn't a discriminated union, so an `earned` card's `awardedAt` (`string |
// null`) doesn't narrow for TypeScript even though it's non-null at runtime — an explicit null-safe
// helper is required instead of a bare `new Date(card.awardedAt)` (carried over from
// `BadgeCategoryGroup.tsx`'s own version of this note, now that this module owns the sort).
const earnedAtMs = (card: BadgeCardViewModel): number =>
  card.awardedAt === null ? 0 : new Date(card.awardedAt).getTime();

/**
 * Orders the flat card list — "flat ordered array" IS the view-model contract
 * (overview-rebuild-pr3-plan.md "Derived data lives in the builder, not the view"), so ordering lives
 * here rather than in a standalone orderer with one caller, which would be the new abstraction
 * `CLAUDE.md` forbids without approval. Earned first, most-recently-awarded first; then locked, with
 * next-tier progress cards (`pointsToNext !== null`) ahead of plain locked cards.
 *
 * Partitioned exactly ONCE, on `earned` — a card that is both earned and still flagged as its
 * ladder's own next tier (`earned` and `pointsToNext` are computed independently, so a never-revoked
 * badge can be both) lands in the earned group only, and can never also appear in a locked group under
 * the same React key. This is what retires `BadgeCategoryGroup`'s old rule-1/rule-2 disjointness guard
 * (its own `!earned` exclusion on the progress-card filter) — a single list partitioned once on
 * `earned` cannot double-count by construction.
 */
const orderBadgeCards = (cards: BadgeCardViewModel[]): BadgeCardViewModel[] => {
  const earned = cards.filter((card) => card.earned).sort((a, b) => earnedAtMs(b) - earnedAtMs(a));
  const locked = cards.filter((card) => !card.earned);
  const nextTierLocked = locked.filter((card) => card.pointsToNext !== null);
  const otherLocked = locked.filter((card) => card.pointsToNext === null);

  return [...earned, ...nextTierLocked, ...otherLocked];
};

/**
 * Pure join of the badge catalog against the user's earned badges (D3), filtering out retired
 * definitions nobody can earn (D4), computing each card's distance-to-go (`computePointsToNext`), and
 * ordering the result (`orderBadgeCards`, above). No mutation; this function's own return is a new
 * array every call.
 *
 * Returns a flat, ordered array, not the old per-category `Record<BADGE_CATEGORY, ...>` — that
 * shape's only stated justification ("category buckets are always present, even empty, so the view can
 * render nothing for an empty one") died with `BadgeCategoryGroup`, its one remaining consumer
 * (overview-rebuild-pr3-plan.md "Derived data lives in the builder, not the view"); the flat grid has
 * no per-category rendering left to serve.
 *
 * Known, unfixed blind spot (D3): a soft-deleted (`is_deleted`) definition someone already earned
 * stays in `badges.earned` (the api never filters that history), but the catalog itself excludes
 * soft-deleted rows — so a definition-driven join like this one can't surface such a trophy at all,
 * since it never appears in `definitions` to iterate over. Documented as currently unreachable on the
 * api side (open-follow-up #29); not fixed here. Consequence for callers: `badges.earned.length` can
 * therefore EXCEED the number of earned cards in the returned array — never use it as an earned-count
 * numerator (see `buildCategorySummaries`, which reads both of its counts off this array instead).
 */
export const buildBadgeCards = (definitions: BadgeDefinition[], badges: UserBadgesResponse): BadgeCardViewModel[] => {
  const earnedByDefinitionId = new Map<number, EarnedBadge>(
    badges.earned.map((earnedBadge): [number, EarnedBadge] => [earnedBadge.badge_definition_id, earnedBadge]),
  );

  const cards: BadgeCardViewModel[] = [];

  definitions.forEach((definition) => {
    const earnedBadge = earnedByDefinitionId.get(definition.id);
    const earned = earnedBadge !== undefined;

    // D4: a retired definition is a dead end if shown locked (no "next" concept explains why it's
    // unreachable) but a legitimate trophy if already earned — keep iff active or already earned.
    if (!definition.is_active && !earned) {
      return;
    }

    cards.push({
      definitionId: definition.id,
      code: definition.code,
      name: definition.name,
      description: definition.description,
      category: definition.category,
      rarity: definition.rarity,
      iconUrl: definition.icon_url,
      earned,
      awardedAt: earnedBadge ? earnedBadge.awarded_at : null,
      pointsToNext: computePointsToNext(definition, badges),
    });
  });

  return orderBadgeCards(cards);
};

const MAX_VISIBLE_EARNED = 5;
const MAX_VISIBLE_LOCKED = 5;

export interface SplitBadgeCards {
  visibleCards: BadgeCardViewModel[];
}

/**
 * Takes the already-ordered flat list from `buildBadgeCards` and returns the fixed preview slice the
 * Overview's Badges block renders: up to 5 earned, then up to 5 locked.
 *
 * Partitions on `earned` INTERNALLY via `filter` — never a prefix slice of an assumed-already-sorted
 * array, which would express the same partition in two places and give this function an unstated
 * sorted-input precondition nothing enforces.
 *
 * Up to 5 and up to 5, rather than a flat cap of 10: that reproduces the design frame literally and
 * degrades sensibly (2 earned gives a short first row plus a full locked row; 0 earned gives one locked
 * row only), where a flat 10 would render ten earned cards and no locked row at 12 earned, hiding the
 * next-tier cards — the only locked ones a user can act on — from exactly the users with the most badges.
 *
 * There is no expanded branch and no hidden/earned/total count any more (all-badges-screen-plan.md,
 * See-all PR2): the block's "See all" NAVIGATES to the dedicated Badges screen instead of toggling this
 * list open in place, and that screen derives its own per-category counts with `buildCategorySummaries`
 * rather than reading them from here. What remains is one slice, always taken.
 *
 * Like `buildBadgeCards`, this returns a new array every call — it never hands back the caller's own
 * `cards` by reference.
 */
export const splitBadgeCards = (cards: BadgeCardViewModel[]): SplitBadgeCards => {
  const earned = cards.filter((card) => card.earned);
  const locked = cards.filter((card) => !card.earned);

  return {
    visibleCards: [...earned.slice(0, MAX_VISIBLE_EARNED), ...locked.slice(0, MAX_VISIBLE_LOCKED)],
  };
};
